/**
 * SALAR Technologies LLC — Public Route Registry
 *
 * Single source of truth for every public route on salartechnologies.com.
 * Used as the canonical reference for sitemap.xml, redirect aliases, navigation,
 * and the internal SEO audit (`npm run seo:audit`).
 *
 * SCHEMA (per route):
 *   path             — Route as it appears in <Route path=...> in App.js
 *   label            — Human-readable nav/UI label
 *   canonical        — Full canonical URL (https://www.salartechnologies.com/...)
 *   title            — Recommended <title> for the page
 *   description      — Recommended meta description (~155 chars)
 *   sitemapInclude   — Whether this URL appears in /sitemap.xml
 *   noindex          — Whether this page sets <meta name="robots" content="noindex">
 *   redirectAlias    — Optional list of alias paths that 308 to this route
 */

export const PUBLIC_ROUTES = [
  {
    path: '/',
    label: 'Home',
    canonical: 'https://www.salartechnologies.com/',
    title: 'SALAR Technologies LLC | Semiconductor Reliability & Supply-Chain Intelligence',
    description: 'SALAR Technologies LLC provides engineering-led semiconductor reliability, supply-chain intelligence, provenance control, and manufacturing-stability decision support.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/engineering-advancement-medical-science',
    label: 'Medical Science Research',
    canonical: 'https://www.salartechnologies.com/engineering-advancement-medical-science',
    title: 'Engineering Advancement in Medical Science | SALAR Technologies',
    description: 'Independent conceptual research directions in type 2 diabetes remission and HIV-1/HIV-2 cure science, with two public research briefs.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/about',
    label: 'About',
    canonical: 'https://www.salartechnologies.com/about',
    title: 'About SALAR Technologies LLC | Engineering-First Reliability Intelligence',
    description: 'Learn about SALAR Technologies LLC, an Oregon engineering R&D company focused on electronics reliability, semiconductor systems, and supply-chain intelligence.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/vikrant-salar',
    label: 'Founder & Principal',
    canonical: 'https://www.salartechnologies.com/vikrant-salar',
    title: 'Vikrant Salar | Founder & Principal, SALAR Technologies LLC',
    description: 'Meet Vikrant Salar, Founder & Principal of SALAR Technologies LLC—engineer, inventor, author, and technical program leader with 21 years of experience.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/capabilities',
    label: 'Capabilities',
    canonical: 'https://www.salartechnologies.com/capabilities',
    title: 'Capabilities | Electronics Reliability & Semiconductor Supply-Chain Analytics',
    description: 'Explore SALAR capabilities in board reliability, predictive failure analytics, semiconductor supply-chain risk, provenance control, and manufacturing stability.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/total-solution-architecture',
    label: 'Total Solution',
    canonical: 'https://www.salartechnologies.com/total-solution-architecture',
    title: 'SALAR Total Solution Architecture | Evidence-to-Decision Intelligence',
    description: 'See how SALAR converts board, supplier, provenance, process, and field evidence into structured risk scores and decision-trace reports.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/salar-resilience-terrain',
    label: 'Interactive Demo',
    canonical: 'https://www.salartechnologies.com/salar-resilience-terrain',
    title: 'SALAR Resilience Terrain Explorer | Public Risk Demo',
    description: 'Explore the SALAR Resilience Terrain Explorer, a public-safe demo for electronics risk, provenance, thermal stress, and decision-trace logic.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/interactive-demo'],
  },
  {
    path: '/salar-graph',
    label: 'Salar Graph Research',
    canonical: 'https://www.salartechnologies.com/salar-graph',
    title: 'Salar Graph | AI-Assisted Reliability & Materials Discovery',
    description: 'Explore SALAR Technologies LLC’s public-safe Salar Graph research initiative for AI-assisted reliability, materials discovery, semiconductor resilience, and graph analytics.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/salargraph'],
  },
  {
    path: '/government-defense',
    label: 'Government & Defense',
    canonical: 'https://www.salartechnologies.com/government-defense',
    title: 'Government & Defense | Mission Electronics Reliability & Supply Assurance',
    description: 'SALAR supports government and defense-aligned electronics reliability, mission assurance, provenance review, supply-chain risk, and sustainment analytics.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/international-procurement',
    label: 'International',
    canonical: 'https://www.salartechnologies.com/international-procurement',
    title: 'International Semiconductor Resilience | SALAR Technologies LLC',
    description: 'SALAR frames semiconductor resilience through global hubs, trusted corridors, supply-chain visibility, critical materials, and mission electronics continuity.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/international'],
  },
  {
    path: '/products',
    label: 'Products',
    canonical: 'https://www.salartechnologies.com/products',
    title: 'Products | SALAR Technologies LLC',
    description: 'Explore SALAR Technologies physical-product programs, beginning with the patent-pending GPMI One Cable unified data and power interconnect architecture.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/products/gpmi-one-cable',
    label: 'GPMI One Cable',
    canonical: 'https://www.salartechnologies.com/products/gpmi-one-cable',
    title: 'GPMI One Cable | Unified Data + Power Interconnect | SALAR Technologies',
    description: 'Patent-pending unified data and power cable architecture with signed channel identity, impedance fingerprint verification, and sequenced power enablement.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/patent-portfolio',
    label: 'Patents & Innovation',
    canonical: 'https://www.salartechnologies.com/patent-portfolio',
    title: 'Patent Portfolio & Innovation | SALAR Technologies LLC',
    description: "Review SALAR's public-safe patent portfolio across board reliability, Ethernet profile governance, supply-chain risk, manufacturing stability, process assurance, AI infrastructure, quantum-safe provenance, and agentic governance.",
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/patents-and-innovation'],
  },
  {
    path: '/licensing',
    label: 'Licensing',
    canonical: 'https://www.salartechnologies.com/licensing',
    title: 'Patent Licensing | SALAR Technologies',
    description: "Eleven patent filings spanning reliability, interconnects, trust, infrastructure and assurance. Public summaries support collaboration and licensing discussions.",
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/license', '/partnerships'],
  },
  {
    path: '/licensing/request',
    label: 'Licensing — Request Brief',
    canonical: 'https://www.salartechnologies.com/licensing/request',
    title: 'Request Licensing Brief | SALAR Technologies',
    description: "Request the full NDA-only licensing brief for SALAR's eight pending US patent applications. SALAR responds within two business days.",
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-1-board-reliability',
    label: 'Licensing — Patent 1 · Board Reliability',
    canonical: 'https://www.salartechnologies.com/licensing/patent-1-board-reliability',
    title: 'Board Reliability Patent Licensing (US App. 18/311,244) — SALAR',
    description: 'Patent-pending board design reliability governance framework. License for defense electronics, networking, EMS, and EDA platforms.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-2-ethernet-profiles',
    label: 'Licensing — Patent 2 · Multi-Lane Ethernet',
    canonical: 'https://www.salartechnologies.com/licensing/patent-2-ethernet-profiles',
    title: 'Multi-Lane Ethernet Patent Licensing (US App. 19/433,978) — SALAR',
    description: 'Profile-based Ethernet connectivity for multi-lane SoC devices. License for SoC, NIC, DPU, and switch ASIC vendors.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-3-supply-chain-graph',
    label: 'Licensing — Patent 3 · Supply-Chain Graph',
    canonical: 'https://www.salartechnologies.com/licensing/patent-3-supply-chain-graph',
    title: 'Supply-Chain Risk Patent Licensing (US App. 19/535,459) — SALAR',
    description: 'Graph-based semiconductor supply-chain risk scoring with closed-loop mitigation.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-4-mfg-stability',
    label: 'Licensing — Patent 4 · Manufacturing Stability',
    canonical: 'https://www.salartechnologies.com/licensing/patent-4-mfg-stability',
    title: 'Manufacturing Stability Patent Licensing (US App. 19/534,285) — SALAR',
    description: 'Closed-loop manufacturing stability across multiple control domains.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-5-ai-lithography',
    label: 'Licensing — Patent 5 · AI-Certified Lithography',
    canonical: 'https://www.salartechnologies.com/licensing/patent-5-ai-lithography',
    title: 'AI-Certified Lithography Patent Licensing (US App. 19/564,196) — SALAR',
    description: 'AI-governance and certification layer for lithography and process control.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-6-ai-infrastructure',
    label: 'Licensing — Patent 6 · AI Infrastructure Orchestration',
    canonical: 'https://www.salartechnologies.com/licensing/patent-6-ai-infrastructure',
    title: 'AI Infrastructure Orchestration Patent Licensing (US App. 19/685,128) — SALAR',
    description: 'Reliability-aware orchestration for AI infrastructure.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-7-pq-provenance',
    label: 'Licensing — Patent 7 · Post-Quantum Provenance',
    canonical: 'https://www.salartechnologies.com/licensing/patent-7-pq-provenance',
    title: 'Post-Quantum Hardware Provenance Patent Licensing (US App. 19/686,634) — SALAR',
    description: 'Hardware-anchored cryptographic provenance bridging for post-quantum verifiable attestation.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/licensing/patent-8-agentic-settlement-governor',
    label: 'Licensing — Patent 8 · Agentic Settlement Governor',
    canonical: 'https://www.salartechnologies.com/licensing/patent-8-agentic-settlement-governor',
    title: 'Agentic Action Settlement Governor Patent Licensing (US App. 64/085,045) — SALAR',
    description: 'Patent-pending agentic action settlement and decision-trace governance layer for enterprise AI workflows, procurement, logistics, and mission systems.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: [],
  },
  {
    path: '/mission-assurance',
    label: 'Mission Assurance',
    canonical: 'https://www.salartechnologies.com/mission-assurance',
    title: 'Mission Assurance | Electronics Reliability & Provenance Control',
    description: 'SALAR mission assurance connects electronics reliability, source confidence, predictive failure reasoning, manufacturing stability, and traceable technical decisions.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/ai-fabric-assurance',
    label: 'AI Fabric',
    canonical: 'https://www.salartechnologies.com/ai-fabric-assurance',
    title: 'AI Fabric Reliability | Ethernet, Edge AI & Infrastructure Assurance',
    description: 'SALAR AI Fabric work focuses on Ethernet-for-AI readiness, network profile governance, edge AI reliability, infrastructure assurance, and technical decision support.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/ai-fabric'],
  },
  {
    path: '/publications',
    label: 'Publications',
    canonical: 'https://www.salartechnologies.com/publications',
    title: 'Publications | SALAR Technologies Research Library',
    description: 'Browse SALAR public-safe publications, research briefs, technical manuals, capability materials, and semiconductor resilience resources.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/resources',
    label: 'Resources',
    canonical: 'https://www.salartechnologies.com/resources',
    title: 'Resources | Capability Statements, Briefs & Public Materials',
    description: 'Download SALAR public resources, including capability statements, technical briefs, compliance statements, and public-safe research materials.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/compliance',
    label: 'Compliance',
    canonical: 'https://www.salartechnologies.com/compliance',
    title: 'Compliance | Public Compliance Statements & Safeguarding Readiness',
    description: 'Read SALAR public compliance-readiness statements covering supply-chain compliance, Section 889 awareness, and federal safeguarding posture.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/investor-relations',
    label: 'Investor Relations',
    canonical: 'https://www.salartechnologies.com/investor-relations',
    title: 'Strategic Partners & Investor Relations | SALAR Technologies LLC',
    description: 'SALAR Technologies LLC invites strategic partner, pilot customer, qualified investor, licensing, and technology-transition conversations around electronics resilience and AI infrastructure assurance.',
    sitemapInclude: true,
    noindex: false,
    redirectAlias: ['/strategic-partners'],
  },
  {
    path: '/news-updates',
    label: 'News & Updates',
    canonical: 'https://www.salartechnologies.com/news-updates',
    title: 'News & Updates | SALAR Technologies LLC',
    description: 'Follow SALAR Technologies LLC news, website updates, publications, demos, compliance releases, capability launches, and public technical updates.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/investor-relations',
    label: 'Investors & Strategic Partners',
    canonical: 'https://www.salartechnologies.com/investor-relations',
    title: 'Strategic Investors & Partners | SALAR Technologies LLC',
    description: 'SALAR Technologies LLC invites strategic investor, pilot partner, and technology-transition conversations around mission-critical electronics resilience.',
    sitemapInclude: true,
    noindex: false,
  },
  {
    path: '/contact',
    label: 'Contact',
    canonical: 'https://www.salartechnologies.com/contact',
    title: 'Contact SALAR Technologies LLC',
    description: 'Contact SALAR Technologies LLC for electronics reliability, semiconductor supply-chain intelligence, mission assurance, and technical decision-support inquiries.',
    sitemapInclude: true,
    noindex: false,
  },
  // Detail-page fallback for News items without an explicit target. Crawlable
  // but not in sitemap (dynamically generated; OG-tagged via NewsDetail.jsx).
  {
    path: '/news-updates/:id',
    label: 'News Detail',
    canonical: 'https://www.salartechnologies.com/news-updates/{id}',
    title: 'News & Updates Detail | SALAR Technologies LLC',
    description: 'Detail view for a specific SALAR Technologies news or update item.',
    sitemapInclude: false,
    noindex: false,
  },
  // Branded 404 catch-all. Excluded from sitemap and indexing.
  {
    path: '*',
    label: 'Page Not Found',
    canonical: 'https://www.salartechnologies.com/404',
    title: 'Page Not Found | SALAR Technologies LLC',
    description: 'The page you are looking for could not be found. Use the quick links to find what you need on salartechnologies.com.',
    sitemapInclude: false,
    noindex: true,
  },
];

// Convenience exports.
export const SITEMAP_ROUTES = PUBLIC_ROUTES.filter((r) => r.sitemapInclude && !r.path.includes(':') && r.path !== '*');
export const ALIAS_REDIRECTS = PUBLIC_ROUTES
  .filter((r) => Array.isArray(r.redirectAlias) && r.redirectAlias.length > 0)
  .flatMap((r) => r.redirectAlias.map((alias) => ({ from: alias, to: r.path, code: 308 })));
